import { useId } from "react";

import type {
  RulesLanguageView,
  SubstitutionBucketName,
  SubstitutionBucketView,
  SubstitutionGenderForm,
} from "@/lib/rulesContent";
import { Label, Textarea } from "@/ui/Input";
import { LoadingState, UnavailableState } from "@/ui/states";

import { SubstitutionTables } from "./SubstitutionTables";

/**
 * One language's named content: its prompts, then its substitution tables
 * (#910).
 *
 * Presentational and total, on `PartsEditor`'s and `NamedDocumentEditor`'s rule
 * — it owns no draft and performs no request, so the same component renders the
 * authoring surface and the read-only one and `readOnly` is one prop every
 * control reads.
 *
 * THE ORDER IS THE REFERENCE'S. The canonical Rules page opens on one large
 * readable prompt field, so the prompt documents come first and the largest of
 * them — the rules text itself — is the first thing on the screen. The tables
 * follow as the named addition this ticket makes, rather than displacing the
 * field the reference leads with.
 *
 * ONE FIELD PER STORED DOCUMENT, and each is labelled by its PRODUCT name
 * ("Rules", "Personality") rather than by its role key. The label comes from
 * `namedParts.roleProductName`, which is the same copy map the other named
 * surfaces read, so a role the service adds tomorrow gets a humanised name
 * rather than a blank heading.
 */
export function RulesLanguageContent({
  view,
  buckets,
  genderForms,
  substitutions,
  inheritance,
  readOnly,
  readOnlyReason,
  onPrompt,
  onSubstitution,
}: {
  view: RulesLanguageView;
  buckets: readonly SubstitutionBucketView[];
  genderForms: readonly SubstitutionGenderForm[];
  /** Whether the tables may be authored, and why not when they may not. */
  substitutions: SubstitutionsState;
  /**
   * What the service says this version's tables were composed against, or
   * `null` while that read has not landed.
   *
   * Rendered as a sentence rather than swallowed: `base` and `unavailable` mean
   * very different things for what "Inherited" would have meant, and a table
   * whose composition is one of them must not imply the other.
   */
  inheritance: "base" | "merged" | "unavailable" | null;
  readOnly: boolean;
  readOnlyReason: string | null;
  onPrompt: (documentId: string, text: string) => void;
  onSubstitution: (
    bucket: SubstitutionBucketName,
    find: string,
    replacement: string | null,
  ) => void;
}) {
  const substitutionsHeadingId = useId();

  return (
    <div className="space-y-6">
      {readOnly && readOnlyReason ? (
        <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
          {readOnlyReason}
        </p>
      ) : null}

      {view.prompts.length === 0 ? (
        <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
          This language carries no prompt yet.
        </p>
      ) : (
        view.prompts.map((prompt) => (
          <PromptField
            key={prompt.document.id}
            label={prompt.label}
            text={prompt.document.text}
            readOnly={readOnly}
            onChange={(text) => onPrompt(prompt.document.id, text)}
          />
        ))
      )}

      <section aria-labelledby={substitutionsHeadingId} className="space-y-3">
        <div className="space-y-1">
          <h2
            id={substitutionsHeadingId}
            className="text-base font-medium text-foreground"
          >
            Substitutions
          </h2>
          <p className="text-sm text-muted-foreground">
            Literal text the assistant&apos;s replies are corrected with after
            they are generated. Each entry finds exact words and replaces them;
            an empty replacement removes the words.
          </p>
          {substitutions.kind !== "ready" || inheritance === null ? null : (
            <p className="text-xs text-muted-foreground">
              {INHERITANCE_SENTENCES[inheritance]}
            </p>
          )}
        </div>

        {substitutions.kind === "loading" ? (
          <LoadingState label="Loading what this rule inherits" rows={3} />
        ) : substitutions.kind === "unavailable" ? (
          // The failure itself is reported by the caller, which owns the read
          // and its recovery. What belongs here is why the tables are absent
          // rather than empty — an empty table would say the runtime applies
          // nothing.
          <UnavailableState
            title="These substitutions cannot be shown yet"
            description="What this rule inherits from the base library has not been read, so the tables would be neither complete nor safe to edit: saving one would replace the stored document with what this page could make of it."
          />
        ) : substitutions.kind === "unreadable" ? (
          <UnavailableState
            title="These substitutions cannot be shown"
            description={
              substitutions.baseAsset
                ? UNREADABLE_BASE_ASSET
                : UNREADABLE_DESCRIPTIONS[substitutions.source]
            }
          />
        ) : buckets.length === 0 ? (
          <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
            This language has no substitution tables.
          </p>
        ) : (
          <SubstitutionTables
            buckets={buckets}
            genderForms={genderForms}
            readOnly={readOnly}
            // `inheritance` is the service's answer, and `null` means it has not
            // arrived. Passing that through rather than defaulting it is what
            // keeps a reset control from asserting what base holds before base
            // has been read.
            composed={inheritance !== null}
            onSet={onSubstitution}
          />
        )}
      </section>
    </div>
  );
}

/**
 * Whether the tables may be authored, and why not when they may not.
 *
 * FOUR VALUES BECAUSE THERE ARE FOUR ANSWERS, and only the first authors.
 * `loading` and `unavailable` are facts about the READ — the composition is not
 * known yet, or could not be — and both withhold the tables, because a table
 * built from the draft alone is one whose Save replaces the stored document with
 * whatever this browser could make of it. `unreadable` is the SERVICE's verdict
 * about the stored documents, and it carries the side, because the side is what
 * a clinician can act on.
 */
export type SubstitutionsState =
  | { kind: "ready" }
  | { kind: "loading" }
  | { kind: "unavailable" }
  | {
      kind: "unreadable";
      source: "own" | "base" | "both";
      /**
       * Whether the version on screen IS base canon's own rule.
       *
       * Carried because it changes the advice and the service deliberately does
       * not carry a fourth `source` for it: on base canon the two sides are ONE
       * stored document, so the source is `own` and `inheritance` is what says
       * the asset is base canon. Without this the surface would tell a clinician
       * to make an editable copy of the base library to escape the base
       * library's own broken document.
       */
      baseAsset: boolean;
    };

/**
 * What an unreadable document means for the clinician, per side.
 *
 * THE SIDE CHANGES THE ADVICE, which is why one sentence could not serve both.
 * Their own document blocks publishing this rule and an editable copy starts
 * clean; base canon's does not block anything here and a copy would inherit the
 * same broken base — so recommending one would send them somewhere that does not
 * help.
 */
const UNREADABLE_BASE_ASSET =
  "The base library's own rule stores substitutions for this language in a shape the Editor Service does not recognise, so what every project inherits from it is not known here. This is the base library itself, so nothing on this screen and no editable copy can repair it — an administrator has to.";

const UNREADABLE_DESCRIPTIONS: Record<"own" | "base" | "both", string> = {
  own: "This rule stores substitutions for this language in a shape the Editor Service does not recognise, so what the runtime applies is not known here. Nothing on this screen alters the stored document, and publishing a new version of this rule is refused until it is readable — make an editable copy to start from the base library's substitutions instead.",
  base: "The base library's own rule stores substitutions for this language in a shape the Editor Service does not recognise, so what this rule inherits is not known here. This rule's own content is unaffected and a new version of it can still be published; an editable copy would inherit the same document, so it would not help. This needs an administrator to repair the base library.",
  both: "Both this rule and the base library store substitutions for this language in a shape the Editor Service does not recognise, so what the runtime applies is not known here. Nothing on this screen alters either document, and publishing a new version of this rule is refused until its own document is readable. The base library needs an administrator to repair it.",
};

/**
 * What each composition state means for the rows below it, in one sentence.
 *
 * Written out per state rather than assembled, because the three differ in what
 * a clinician should DO next: on base canon there is nothing to inherit from and
 * a copy is the way to change anything; on a merged view removing an entry
 * restores a value; on an unavailable one the tables are incomplete and saying
 * "Inherited" of anything would be a claim the service did not make.
 */
const INHERITANCE_SENTENCES: Record<"base" | "merged" | "unavailable", string> =
  {
    base: "These are the base library's own substitutions. Make an editable copy to change any of them for a project.",
    merged:
      "Entries from the base library are shown alongside the ones this rule changes. Leaving an entry out of this rule keeps the base library's — it is never removed from the runtime.",
    unavailable:
      "The base library's own rule could not be resolved here, so these are only the entries this rule carries. What the runtime inherits is not shown.",
  };

function PromptField({
  label,
  text,
  readOnly,
  onChange,
}: {
  label: string;
  text: string;
  readOnly: boolean;
  onChange: (text: string) => void;
}) {
  const fieldId = useId();

  return (
    <div className="space-y-1.5">
      <Label htmlFor={fieldId}>{label}</Label>
      {/* `dir="auto"` for `SubstitutionTables`' reason: this asset is bilingual
          and the same component renders the Hebrew and the English document, so
          the direction follows the content rather than a hard-coded choice. */}
      <Textarea
        id={fieldId}
        dir="auto"
        rows={22}
        value={text}
        readOnly={readOnly}
        aria-disabled={readOnly || undefined}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
